import * as L from "leaflet"
import type { Map } from "leaflet"
import fp from "lodash/fp"
import type { GeometryCollection, LineString, Point, Position } from "geojson"

import towerUrl from "images/map/tower.png"
import warningIconUrl from "images/map/warning.svg"
import sentriV4IconUrl from "images/map/sentri-v4.png"
import triggeredSentriV4IconUrl from "images/map/sentri-v4-alert.png"
import sentriV23IconUrl from "images/map/sentri-v2-v3.png"
import triggeredSentriV23IconUrl from "images/map/sentri-v2-v3-alert.png"
import type {
  FeaturePropertiesType,
  DeviceLocation,
  LatLonRecord,
  GeoJSONLayersData,
  OrgGeoJSON,
  Coords,
  LatLonRecordTelemetry,
} from "types/geolocation.types"
import type { RecordType } from "types/dashboard.types"

import type { IDevsWRelsByIdWStates } from "types/device.types"
import defaultIconUrl from "images/map/marker.png"
import defaultIconRetinaUrl from "images/map/marker-x2.png"
import triggeredIconUrl from "images/map/marker-red.png"
import triggeredIconRetinaUrl from "images/map/marker-red-x2.png"
import { isVersionAboveOrEqual, isVirtualDevice } from "./devices"

//MAP ICONS
export const TRIGGERED_DEVICE_MARKER = L.icon({
  iconUrl: triggeredIconUrl,
  iconRetinaUrl: triggeredIconRetinaUrl,
  iconSize: [25, 37],
})

export const SENTRI_V4_ICON = L.icon({
  iconUrl: sentriV4IconUrl,
  iconSize: [30, 30],
})
export const TRIGGERED_SENTRI_V4_ICON = L.icon({
  iconUrl: triggeredSentriV4IconUrl,
  iconSize: [34, 30],
  iconAnchor: [17, 17],
})

export const SENTRI_V2_3_ICON = L.icon({
  iconUrl: sentriV23IconUrl,
  iconSize: [30, 30],
})
export const TRIGGERED_SENTRI_V2_3_ICON = L.icon({
  iconUrl: triggeredSentriV23IconUrl,
  iconSize: [34, 30],
  iconAnchor: [17, 17],
})

export const DEFAULT_MARKER = L.icon({
  iconUrl: defaultIconUrl,
  iconRetinaUrl: defaultIconRetinaUrl,
  iconSize: [25, 37],
})

export const TOWER = L.icon({
  iconUrl: towerUrl,
  iconRetinaUrl: towerUrl,
  iconSize: [22, 22],
})

export const WARNING_ICON = L.icon({
  iconUrl: warningIconUrl,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
})

export const featureProperties: FeaturePropertiesType = {
  Powerline: ["kind", "name", "id", "voltage_nominal", "ampacity_nominal", "length"],
  PowerlineSpan: ["kind", "name", "id", "ampacity_nominal", "length", "temperature_max"],
  PowerlineTower: ["kind", "name", "id", "height", "type"],
  Device: ["kind", "name", "less_id", "device_version"],
}

// The unit each numeric property is read in.
export const featurePropertyUnits: Record<string, string> = {
  ampacity_nominal: "A",
  voltage_nominal: "V",
  length: "m",
  height: "m",
  temperature_max: "°C",
}

// Cached, because a new icon identity makes react-leaflet rebuild every marker's
// DOM. A WeakMap, so the cache doesn't keep unused icons in memory.
const scaledIconCache = new WeakMap<L.Icon, Record<number, L.Icon>>()

// A smaller clone of an image icon, keeping its url and retina options.
export const getScaledIcon = (icon: L.Icon, scale: number): L.Icon => {
  const byScale = scaledIconCache.get(icon) ?? {}
  const cached = byScale[scale]
  if (cached) return cached

  const opts = icon.options
  const size = opts.iconSize as [number, number] | undefined
  const anchor = opts.iconAnchor as [number, number] | undefined
  const scaled = L.icon({
    ...opts,
    iconSize: size ? [size[0] * scale, size[1] * scale] : undefined,
    iconAnchor: anchor ? [anchor[0] * scale, anchor[1] * scale] : undefined,
  })

  byScale[scale] = scaled
  scaledIconCache.set(icon, byScale)
  return scaled
}

export const getMarkerIcon = (triggered: boolean, version?: string, target?: string) => {
  if (version && target) {
    if (triggered) {
      return isVersionAboveOrEqual(version, target)
        ? TRIGGERED_SENTRI_V4_ICON
        : TRIGGERED_SENTRI_V2_3_ICON
    } else {
      return isVersionAboveOrEqual(version, target) ? SENTRI_V4_ICON : SENTRI_V2_3_ICON
    }
  } else {
    if (triggered) {
      return TRIGGERED_DEVICE_MARKER
    } else {
      return DEFAULT_MARKER
    }
  }
}

export const showTooltips = (mapLayer: Map, shouldShow: boolean) => {
  mapLayer?.eachLayer((layer) => {
    if (layer.getTooltip()) {
      if (shouldShow) {
        layer.openTooltip()
      } else {
        layer.closeTooltip()
      }
    }
  })
}

export const getLatLonDataFromRecord = (data: RecordType[]): LatLonRecordTelemetry[] => {
  const formattedTelemetryData: LatLonRecordTelemetry[] = fp.flow(
    fp.filter(({ _field }) => _field === "lat" || _field === "lon"),
    fp.groupBy("id"),
    fp.toPairs,
    fp.map(([_, value]) => {
      let joinedData = { name: "Location" }
      fp.each((obj) => {
        const { _field, _value, ...rest } = obj
        if (_field === "lat") {
          joinedData = { ...rest, ...joinedData, lat: _value }
        } else if (_field === "lon") {
          joinedData = { ...rest, ...joinedData, lon: _value }
        }
      }, value)
      return [_, joinedData]
    }),
    fp.fromPairs,
    fp.values,
    fp.filter((obj: LatLonRecordTelemetry) => {
      return typeof obj.lat === "number" && typeof obj.lon === "number"
    }),
  )(data)

  return formattedTelemetryData
}

export const dropVirtualDevices = (deviceLocations: DeviceLocation[]) => {
  return deviceLocations.filter(
    (deviceLocation) => !isVirtualDevice(deviceLocation.device.device_type),
  )
}

export const formatDeviceLatLonData = (
  latlonData: LatLonRecord[] | Coords[],
  devsWRelsByIdWStates: IDevsWRelsByIdWStates,
): DeviceLocation[] => {
  return latlonData?.map((obj) => {
    const { groups, settingsStates, device } = devsWRelsByIdWStates[obj.id] || {}
    const extras = fp.omitBy(fp.isNil, {
      groups,
      settingsStates,
    })
    return {
      device: { ...device, less_id: Number(obj.id) },
      ...extras,
      location: { ...obj },
    }
  })
}

export const getDevicePosition = (
  deviceLocation: DeviceLocation,
): L.LatLngTuple | undefined => {
  const { location, device } = deviceLocation
  if (!fp.isNil(location.lat) && !fp.isNil(location.lon)) {
    return [location.lat, location.lon]
  } else if (
    device.settings &&
    device.settings.geo_location &&
    "latitude" in device.settings.geo_location &&
    "longitude" in device.settings.geo_location
  ) {
    return [device.settings.geo_location.latitude, device.settings.geo_location.longitude]
  } else {
    return
  }
}

export const getMapCenterFromDeviceLocation = (
  data: DeviceLocation[] | undefined | null,
): L.LatLngTuple | null => {
  const position = data?.length && getDevicePosition(data[0])
  return position ? position : null
}

// Whether there is a route to draw. The dashboard's no-data message and the
// span map's gate both read this, so they can't disagree.
export const hasPowerlineRoute = (geoData: GeoJSONLayersData | undefined): boolean =>
  (geoData?.Powerline?.features?.length ?? 0) > 0

/**
 * Bounds that fit a whole powerline. Prefers the span layer, since that is what
 * the panel draws, and falls back to the route. Null when there is nothing.
 */
export const getGeoBounds = (
  geoData: GeoJSONLayersData | undefined,
): L.LatLngBounds | null => {
  if (!geoData) return null

  const points: L.LatLngTuple[] = []
  for (const feature of geoData.PowerlineSpan?.features ?? []) {
    points.push(...formatLineStringCoordinates(feature.geometry.coordinates))
  }
  if (points.length === 0) {
    for (const feature of geoData.Powerline?.features ?? []) {
      points.push(...formatMultiStringCoordinates(feature.geometry.coordinates).flat())
    }
  }

  return points.length ? L.latLngBounds(points) : null
}

export const getMapCenterFromGeo = (
  geoData: GeoJSONLayersData | undefined,
): L.LatLngTuple | null => {
  if (!geoData) return null

  const deviceCoords = geoData.Device?.features[0]?.geometry.coordinates
  if (deviceCoords) return [deviceCoords[1], deviceCoords[0]]

  const towerGeometry = geoData.PowerlineTower?.features[0]?.geometry
  if (!towerGeometry) return null

  const towerCoords =
    towerGeometry.type === "Point"
      ? towerGeometry.coordinates
      : towerGeometry.type === "GeometryCollection"
        ? (
            fp.find((g: Point | LineString) => g.type === "Point")(
              towerGeometry.geometries,
            ) as Point | undefined
          )?.coordinates
        : null

  return towerCoords ? [towerCoords[1], towerCoords[0]] : null
}

export const formatLineStringCoordinates = (data: Position[]): L.LatLngTuple[] =>
  data.map(([lng, lat]) => [lat, lng] as L.LatLngTuple)

export const formatMultiStringCoordinates = (data: Position[][]): L.LatLngTuple[][] =>
  data.map((innerArray) => formatLineStringCoordinates(innerArray))

export const formatPointCollectionCoordinates = (
  data: GeometryCollection<Point | LineString>,
): L.LatLngTuple => {
  const [lng, lat] = data.geometries[0].coordinates as Position
  return [lat, lng]
}

export const formatPointCoordinates = (data: Point): L.LatLngTuple => [
  data.coordinates[1],
  data.coordinates[0],
]

export const getCoordsFromGeoByLessId = (geo: OrgGeoJSON, lessId: number): Coords[] => {
  const coords = geo.Device.features.find(
    (feature) => feature.properties.less_id === lessId,
  )?.geometry.coordinates
  return coords ? [{ id: String(lessId), lat: coords[1], lon: coords[0] }] : []
}

export const getAndFormatLastLatLon = (
  location: LatLonRecord[] | Coords[],
): { lat: string; lon: string } | undefined => {
  return fp.flow(
    fp.map(({ lat, lon }: LatLonRecord | Coords) => ({
      lat: String(lat),
      lon: String(lon),
    })),
    fp.last,
  )(location) as { lat: string; lon: string } | undefined
}

export const checkEmptyGeoData = (data: GeoJSONLayersData | undefined): boolean =>
  fp.isEmpty(data?.Device?.features) &&
  fp.isEmpty(data?.Powerline?.features) &&
  fp.isEmpty(data?.PowerlineSpan?.features) &&
  fp.isEmpty(data?.PowerlineTower?.features)

// Halfway along the polyline, not the middle vertex — on a two-point span that
// vertex is one of the towers.
export const getLineMidpoint = (coordinates: L.LatLngTuple[]): L.LatLngTuple | null =>
  coordinates.length === 1
    ? coordinates[0]
    : calculateLimitingSpanIconPosition(coordinates, 0)

export const calculateLimitingSpanIconPosition = (
  coordinates: L.LatLngTuple[],
  offsetDistance: number = 0.0005,
): L.LatLngTuple | null => {
  if (coordinates.length < 2) return null

  // Calculate total length
  let totalLength = 0
  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lat1, lng1] = coordinates[i]
    const [lat2, lng2] = coordinates[i + 1]
    const dx = lng2 - lng1
    const dy = lat2 - lat1
    totalLength += Math.sqrt(dx * dx + dy * dy)
  }

  // Find midpoint
  const targetLength = totalLength / 2
  let accumulatedLength = 0

  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lat1, lng1] = coordinates[i]
    const [lat2, lng2] = coordinates[i + 1]
    const dx = lng2 - lng1
    const dy = lat2 - lat1
    const segmentLength = Math.sqrt(dx * dx + dy * dy)

    if (accumulatedLength + segmentLength >= targetLength) {
      const ratio = (targetLength - accumulatedLength) / segmentLength
      const midLat = lat1 + dy * ratio
      const midLng = lng1 + dx * ratio

      // Calculate perpendicular offset (rotate direction by 90°)
      const length = Math.sqrt(dx * dx + dy * dy)
      if (length === 0) return [midLat, midLng]

      const perpLat = (-dy / length) * offsetDistance
      const perpLng = (dx / length) * offsetDistance

      return [midLat + perpLng, midLng + perpLat]
    }
    accumulatedLength += segmentLength
  }

  return null
}
